// Debug and test hooks (§28): `window.__SHOVEOFF__`, always present, the `?debug=1` stats overlay,
// and the contract of the `?debug=cosmetics|vfx|audio` verification pages. `simHash` is the shared
// bots-only probe, so Node and Chrome run the identical simulation by construction (§30.4); it is
// re-exported for the Node side of the hash test.
import { simHash } from '@shared/bots/headless';
import type { SimEvent } from '@shared/events';
import type { ClientMsg } from '@shared/protocol/messages';
import { PROTOCOL_VERSION } from '@shared/protocol/version';
import { onSim, type Bus } from './core/bus';
import './debug.css';
import type { BeanView, FrameSource, FrameView } from './game/FrameView';
import type { QualityTier, Renderer } from './render/Renderer';
import type { ScreenId } from './ui/contracts';

export { simHash };

export const CLIENT_VERSION = `1.0.0+p${PROTOCOL_VERSION}`;

const EVENT_RING = 256;
const OVERLAY_REFRESH_MS = 250;
const QUALITIES: readonly QualityTier[] = ['high', 'medium', 'low', 'potato'];

export interface PredErrStats { p50: number; p95: number; max: number; hist: number[]; /** Reconciliations sampled (additive, merge). */ count: number }
export interface DebugNet { rtt: number; jitter: number; predErr: PredErrStats; snapBytesAvg: number; online: boolean }
export interface DebugTick { room: number; round: number; render: number; local: number }
export interface DebugRoom { code: string; phase: string; roundIndex: number; players: number }

/** The §28 hook object. */
export interface ShoveOffDebug {
  version: string;
  ready: boolean;
  screen: () => ScreenId;
  room: () => DebugRoom | null;
  localSlot: () => number;
  tick: () => DebugTick;
  net: () => DebugNet;
  perf: () => { fps: number; frameMs: number; drawCalls: number; triangles: number; tier: string };
  beans: () => BeanView[];
  /** Additive (INT-C): the presented frame's post grade ('game' from the match's final KO: GAME!). */
  grade: () => FrameView['grade'];
  /** Additive (INT-C): the replay on screen ('instant' | 'bonkcam' | 'potm'), or null. */
  replay: () => string | null;
  events: (n?: number) => SimEvent[];
  hash: () => number;
  simHash: (seed: number, ticks: number, mapId: number, modeId: number) => number;
  screenshotReady: () => boolean;
  setQuality: (t: string) => void;
  send: (msg: ClientMsg) => void;
}

/** What the boot layer exposes to the hooks; each getter reads the active frame source. */
export interface DebugSources {
  screen(): ScreenId;
  room(): DebugRoom | null;
  view(): FrameView;
  tick(): DebugTick;
  net(): DebugNet;
  hash(): number;
  screenshotReady(): boolean;
  send(msg: ClientMsg): void;
}

/**
 * A `?debug=` verification page (§28), shown instead of the menu flow. Each one lives with the
 * package it verifies: cosmetics (WP5), vfx (WP7), audio (WP8). `?debug=solids` (WP6) is a
 * renderer overlay on the normal flow instead.
 */
export interface VerificationPage {
  /** The stage the page shows, replacing the menu diorama; null when the page draws itself. */
  readonly source: FrameSource | null;
  /** The page covers the screen with its own drawing; the game canvas is not rendered. */
  readonly hidesGame: boolean;
  /** Built and drawn at least once (screenshot readiness). */
  ready(): boolean;
  /** Once per frame, after the frame's view is built. */
  update(view: FrameView): void;
}

declare global {
  interface Window { __SHOVEOFF__: ShoveOffDebug }
}

function fmt(n: number, digits = 0): string {
  return Number.isFinite(n) ? n.toFixed(digits) : '–';
}

export interface DebugHooks {
  /** Boot complete: sets `__SHOVEOFF__.ready`. */
  markReady(): void;
  /** Once per frame (§23.2). */
  update(view: FrameView): void;
}

/**
 * Installs `window.__SHOVEOFF__` and, when `overlay` is true (`?debug=1` or the "Show FPS & ping"
 * setting), the stats overlay inside `uiRoot`.
 */
export function installDebug(bus: Bus, renderer: Renderer, src: DebugSources, uiRoot: HTMLElement, overlay: () => boolean): DebugHooks {
  const ring: SimEvent[] = [];
  let head = 0;
  onSim(bus, (ev) => {
    const copy = { ...ev };
    if (ring.length < EVENT_RING) ring.push(copy);
    else ring[head] = copy;
    head = (head + 1) % EVENT_RING;
  });

  const hooks: ShoveOffDebug = {
    version: CLIENT_VERSION,
    ready: false,
    screen: () => src.screen(),
    room: () => src.room(),
    localSlot: () => src.view().localSlot,
    tick: () => src.tick(),
    net: () => src.net(),
    perf: () => ({ ...renderer.stats }),
    beans: () => src.view().beans.map((b) => ({ ...b, cosmetics: { ...b.cosmetics } })),
    grade: () => src.view().grade,
    replay: () => src.view().replay?.kind ?? null,
    events: (n = EVENT_RING) => {
      const ordered = ring.length < EVENT_RING ? ring.slice() : [...ring.slice(head), ...ring.slice(0, head)];
      return ordered.slice(Math.max(0, ordered.length - n));
    },
    hash: () => src.hash(),
    simHash,
    screenshotReady: () => src.screenshotReady(),
    setQuality: (t) => {
      const tier = QUALITIES.find((q) => q === t);
      if (!tier) throw new RangeError(`setQuality: unknown tier ${t}`);
      renderer.setQuality(tier);
    },
    send: (msg) => src.send(msg),
  };
  window.__SHOVEOFF__ = hooks;

  const panel = document.createElement('div');
  panel.className = 'stats-overlay';
  panel.hidden = true;
  uiRoot.append(panel);
  let refreshAt = 0;

  return {
    markReady() {
      hooks.ready = true;
    },
    update(view) {
      const show = overlay();
      if (panel.hidden === show) panel.hidden = !show;
      if (!show || view.now < refreshAt) return;
      refreshAt = view.now + OVERLAY_REFRESH_MS;
      const p = renderer.stats;
      const n = src.net();
      panel.textContent = [
        `fps ${fmt(p.fps)}  frame ${fmt(p.frameMs, 1)} ms  tier ${p.tier}`,
        `draws ${p.drawCalls}  tris ${(p.triangles / 1000).toFixed(1)}k`,
        `rtt ${fmt(n.rtt)} ms  jitter ${fmt(n.jitter, 1)} ms  pred p95 ${fmt(n.predErr.p95, 3)} m`,
        `snapshot ${fmt(n.snapBytesAvg)} B  ${n.online ? 'online' : 'offline'}`,
      ].join('\n');
    },
  };
}
